/**
 * Shipping Configuration
 *
 * Centralized shipping settings including free shipping thresholds,
 * flat rates, and delivery estimates.
 *
 * TWO TRUTHS THAT CAN DRIFT APART: the free-shipping threshold this storefront
 * actually GRANTS is Saleor's `maximumOrderPrice = 249.99` on the paid shipping
 * methods, evaluated on the net order amount. `freeShippingThreshold` below (250)
 * is a manually maintained DISPLAY value with no code link to that Saleor field.
 * `scripts/set-shipping-price.mjs` (see its `maximumOrderPrice` handling) keeps
 * the Saleor side in sync automatically when the threshold changes; this object
 * must be updated by hand in the SAME PR. There is no automated cross-check --
 * if you change one, grep for the other before you ship.
 */

/**
 * Where the JSON-LD says we ship and accept returns, per channel. Used ONLY by
 * the Product and Organization JSON-LD (OfferShippingDetails.shippingDestination,
 * MerchantReturnPolicy.applicableCountry).
 *
 * Deliberately NARROWER than the checkout, which is unaffected and still accepts
 * every country of Saleor's "Worldwide" zone (mirrored, unchanged, by the 250
 * entries in `src/checkout/lib/consts/countries.ts`). Publishing that full
 * list made Search Console warn on every product page
 * ("applicableCountry kann höchstens 50-mal angegeben werden", 16.10.2026), and
 * it carried the non-ISO "EU" plus uninhabited territories (AQ, BV, HM, ...).
 * So each channel lists at most 50 ISO 3166-1 alpha-2 codes for its core
 * markets (owner decision D3, 16.10.2026).
 */
const JSON_LD_DESTINATIONS_EU: readonly string[] = [
	// EU-27
	"AT",
	"BE",
	"BG",
	"HR",
	"CY",
	"CZ",
	"DK",
	"EE",
	"FI",
	"FR",
	"DE",
	"GR",
	"HU",
	"IE",
	"IT",
	"LV",
	"LT",
	"LU",
	"MT",
	"NL",
	"PL",
	"PT",
	"RO",
	"SK",
	"SI",
	"ES",
	"SE",
	// Further markets served from the EU channel
	"IS",
	"LI",
	"NO",
	"CH",
	"GB",
	"TR",
	"BR",
];

const JSON_LD_DESTINATIONS_US: readonly string[] = ["US", "PR", "CA", "MX", "AU"];

export const SHIPPING_CONFIG = {
	freeShippingThreshold: {
		eu: 250,
		us: 250,
	},
	flatRate: {
		eu: 9.99,
		us: 9.99,
	},
	deliveryDays: {
		min: 5,
		max: 11,
	},
	/**
	 * GOOGLE SNIPPET ONLY. `deliveryDays` above (5-11) drives every visible
	 * delivery statement (PDP delivery line, cart, /shipping, FAQ). The Product
	 * JSON-LD deliberately publishes a different, shorter figure into the merchant
	 * listing and native agent Markdown: the owner's average across standard and
	 * express shipping (decision 2026-09-02). handlingDays +
	 * snippetTransitDays = 2-6 business days. Nothing in the visible storefront
	 * reads these two blocks; change `deliveryDays` for anything a customer sees.
	 */
	handlingDays: {
		min: 0,
		max: 1,
	},
	snippetTransitDays: {
		min: 2,
		max: 5,
	},
	/** Emit deliveryTime into the Product JSON-LD (Google lists it as recommended). */
	publishDeliveryTime: true,
	/**
	 * Countries the JSON-LD shipping details and return policy apply to, per
	 * channel (max 50 each; the checkout accepts more, see the lists above).
	 */
	destinations: {
		eu: JSON_LD_DESTINATIONS_EU,
		us: JSON_LD_DESTINATIONS_US,
	},
	/**
	 * Mirrors the policy published on /shipping (`shipping.returnsPolicy`):
	 * 14 days after receipt, unopened and in original packaging, customer pays
	 * the return shipping. Structured data only (MerchantReturnPolicy).
	 */
	returns: {
		windowDays: 14,
	},
} as const;
